// src/utils/scoringTemplates.js
// =============================================
// MOTOR DE CÁLCULO FIG — SISTEMA UNIVERSAL
// =============================================
// Nota Final = D + E + A − P (modelo base)
//
// Variantes por disciplina:
//   GAM/GAF:  Final = D + trimMean(E) − P
//   AER:      Final = D + avg(E) + avg(A) − P
//   ACRO:     Final = D + trimMean(E) + avg(A) − P
//   Trampolín: Final = D + trimMean(E) + ToF + HD − P
//   Parkour Speed: Tiempo + P
//   Parkour Freestyle: D + E + A − P
//   Rítmica:  Final = D + trimMean(E) + trimMean(A) − P
// =============================================

/**
 * TIPOS DE COLUMNA:
 * - difficulty   : D-Score (se suma)
 * - execution    : E-Score (parte de 10, se suma)
 * - artistic     : A-Score (se suma)
 * - penalty      : Penalizaciones (se resta)
 * - bonus        : Bonificación (se suma)
 * - time         : Tiempo (para parkour speed)
 * - time_of_flight : Tiempo de vuelo (trampolín)
 * - h_displacement : Desplazamiento horizontal (trampolín, resta)
 * - score        : Genérico (se suma)
 * - deduction    : Genérico deducción (se resta)
 */

// =============================================
// PLANTILLAS POR DISCIPLINA
// =============================================

// ----- ARTÍSTICA MASCULINA (GAM) -----
const GAM_TEMPLATE = {
  key: 'gam_standard',
  name: 'GAM — Artística Masculina (FIG)',
  discipline: 'artistic',
  category: 'mag',
  formula: 'D + trimMean(E) − P',
  description: 'Panel D evalúa dificultad. Panel E (5-6 jueces) califica ejecución desde 10. Se eliminan nota mayor y menor del Panel E, se promedian las restantes. Se restan penalizaciones.',
  panels: {
    D: { label: 'Panel D — Dificultad', judges: 2, method: 'consensus', startScore: null },
    E: { label: 'Panel E — Ejecución', judges: 5, method: 'trimmed_mean', startScore: 10 },
  },
  columns: [
    { key: 'd_score', label: 'Dificultad', type: 'difficulty', max_value: null, startScore: null, description: 'Valor de dificultad — suma de las mejores habilidades ejecutadas', panel: 'D' },
    { key: 'e_score', label: 'Ejecución', type: 'execution', max_value: 10, startScore: 10, description: 'Ejecución — parte de 10.0, se restan errores técnicos', panel: 'E' },
    { key: 'penalty', label: 'Penalización', type: 'penalty', max_value: null, startScore: null, description: 'Deducciones neutrales: línea, tiempo, vestimenta, asistencia', panel: null },
  ]
};

// ----- ARTÍSTICA FEMENINA (GAF) -----
const GAF_TEMPLATE = {
  key: 'gaf_standard',
  name: 'GAF — Artística Femenina (FIG)',
  discipline: 'artistic',
  category: 'wag',
  formula: 'D + trimMean(E) − P',
  description: 'Igual que GAM. Panel D define dificultad. Panel E (5 jueces) → eliminar extremos → promediar. Se restan penalizaciones.',
  panels: {
    D: { label: 'Panel D — Dificultad', judges: 2, method: 'consensus', startScore: null },
    E: { label: 'Panel E — Ejecución', judges: 5, method: 'trimmed_mean', startScore: 10 },
  },
  columns: [
    { key: 'd_score', label: 'Dificultad', type: 'difficulty', max_value: null, startScore: null, description: 'Valor de dificultad — suma de elementos', panel: 'D' },
    { key: 'e_score', label: 'Ejecución', type: 'execution', max_value: 10, startScore: 10, description: 'Ejecución — parte de 10.0', panel: 'E' },
    { key: 'penalty', label: 'Penalización', type: 'penalty', max_value: null, startScore: null, description: 'Deducciones neutrales', panel: null },
  ]
};

// ----- AERÓBICA (AER) -----
const AER_TEMPLATE = {
  key: 'aer_standard',
  name: 'AER — Aeróbica (FIG)',
  discipline: 'aerobic',
  category: null,
  formula: 'D + avg(E) + avg(A) − P',
  description: 'Dificultad (suma de elementos) + Promedio Ejecución (desde 10) + Promedio Artístico (desde 10) − Penalizaciones.',
  panels: {
    D: { label: 'Panel D — Dificultad', judges: 2, method: 'consensus', startScore: null },
    E: { label: 'Panel E — Ejecución', judges: 4, method: 'average', startScore: 10 },
    A: { label: 'Panel A — Artístico', judges: 4, method: 'average', startScore: 10 },
  },
  columns: [
    { key: 'd_score', label: 'Dificultad', type: 'difficulty', max_value: null, startScore: null, description: 'Suma de elementos de dificultad', panel: 'D' },
    { key: 'e_score', label: 'Ejecución', type: 'execution', max_value: 10, startScore: 10, description: 'Ejecución técnica', panel: 'E' },
    { key: 'a_score', label: 'Artístico', type: 'artistic', max_value: 10, startScore: 10, description: 'Artístico (coreografía, música, presentación)', panel: 'A' },
    { key: 'penalty', label: 'Penalización', type: 'penalty', max_value: null, startScore: null, description: 'Deducciones neutrales', panel: null },
  ]
};

// ----- ACROBÁTICA (ACRO) -----
const ACRO_TEMPLATE = {
  key: 'acro_standard',
  name: 'ACRO — Acrobática (FIG)',
  discipline: 'acrobatic',
  category: null,
  formula: 'D + trimMean(E) + avg(A) − P',
  description: 'Dificultad calculada + Ejecución (eliminar extremos, promediar) + Artístico (promedio simple) − Penalizaciones.',
  panels: {
    D: { label: 'Panel D — Dificultad', judges: 2, method: 'consensus', startScore: null },
    E: { label: 'Panel E — Ejecución', judges: 5, method: 'trimmed_mean', startScore: 10 },
    A: { label: 'Panel A — Artístico', judges: 4, method: 'average', startScore: 10 },
  },
  columns: [
    { key: 'd_score', label: 'Dificultad', type: 'difficulty', max_value: null, startScore: null, description: 'Dificultad de los elementos acrobáticos', panel: 'D' },
    { key: 'e_score', label: 'Ejecución', type: 'execution', max_value: 10, startScore: 10, description: 'Ejecución técnica', panel: 'E' },
    { key: 'a_score', label: 'Artístico', type: 'artistic', max_value: 10, startScore: 10, description: 'Artístico y coreografía', panel: 'A' },
    { key: 'penalty', label: 'Penalización', type: 'penalty', max_value: null, startScore: null, description: 'Deducciones neutrales', panel: null },
  ]
};

// ----- RÍTMICA (GR) -----
const RHYTHMIC_TEMPLATE = {
  key: 'gr_standard',
  name: 'GR — Rítmica (FIG)',
  discipline: 'rhythmic',
  category: null,
  formula: 'D + trimMean(E) + trimMean(A) − P',
  description: 'D (dificultad corporal + aparato) + E (ejecución, eliminar extremos) + A (artístico, eliminar extremos) − Penalizaciones.',
  panels: {
    D: { label: 'Panel D — Dificultad', judges: 2, method: 'consensus', startScore: null },
    E: { label: 'Panel E — Ejecución', judges: 5, method: 'trimmed_mean', startScore: 10 },
    A: { label: 'Panel A — Artístico', judges: 4, method: 'trimmed_mean', startScore: 10 },
  },
  columns: [
    { key: 'd_body', label: 'Dificultad Corporal', type: 'difficulty', max_value: null, startScore: null, description: 'Dificultad corporal (saltos, giros, equilibrios)', panel: 'D' },
    { key: 'd_apparatus', label: 'Dificultad Aparato', type: 'difficulty', max_value: null, startScore: null, description: 'Dificultad de manejo del aparato', panel: 'D' },
    { key: 'e_score', label: 'Ejecución', type: 'execution', max_value: 10, startScore: 10, description: 'Ejecución técnica', panel: 'E' },
    { key: 'a_score', label: 'Artístico', type: 'artistic', max_value: 10, startScore: 10, description: 'Componente artístico', panel: 'A' },
    { key: 'penalty', label: 'Penalización', type: 'penalty', max_value: null, startScore: null, description: 'Deducciones neutrales', panel: null },
  ]
};

// ----- TRAMPOLÍN (GT) -----
const TRAMPOLINE_TEMPLATE = {
  key: 'gt_standard',
  name: 'GT — Trampolín (FIG)',
  discipline: 'trampoline',
  category: null,
  formula: 'D + trimMean(E) + ToF + HD − P',
  description: 'Dificultad + Ejecución (sin extremos) + Tiempo de vuelo + Desplazamiento horizontal − Penalizaciones.',
  panels: {
    D: { label: 'Panel D — Dificultad', judges: 2, method: 'consensus', startScore: null },
    E: { label: 'Panel E — Ejecución', judges: 5, method: 'trimmed_mean', startScore: 10 },
  },
  columns: [
    { key: 'd_score', label: 'Dificultad', type: 'difficulty', max_value: 20, startScore: null, description: 'Valor de dificultad del ejercicio', panel: 'D' },
    { key: 'e_score', label: 'Ejecución', type: 'execution', max_value: 20, startScore: 10, description: 'Ejecución (por salto, de 10)', panel: 'E' },
    { key: 'time_of_flight', label: 'Tiempo de Vuelo', type: 'time_of_flight', max_value: 20, startScore: null, description: 'Tiempo de vuelo medido electrónicamente', panel: null },
    { key: 'h_displacement', label: 'Desplazamiento', type: 'h_displacement', max_value: 10, startScore: null, description: 'Deducción por desplazamiento horizontal', panel: null },
    { key: 'penalty', label: 'Penalización', type: 'penalty', max_value: null, startScore: null, description: 'Deducciones neutrales', panel: null },
  ]
};

// ----- PARKOUR FREESTYLE -----
const PARKOUR_FREESTYLE_TEMPLATE = {
  key: 'pk_freestyle',
  name: 'PK — Parkour Freestyle (FIG)',
  discipline: 'parkour',
  category: 'freestyle',
  formula: 'D + E + A − P',
  description: 'Dificultad + Ejecución + Creatividad/Artístico − Penalizaciones.',
  panels: {
    D: { label: 'Panel D — Dificultad', judges: 2, method: 'consensus', startScore: null },
    E: { label: 'Panel E — Ejecución', judges: 4, method: 'trimmed_mean', startScore: 10 },
    A: { label: 'Panel A — Creatividad', judges: 2, method: 'average', startScore: 10 },
  },
  columns: [
    { key: 'd_score', label: 'Dificultad', type: 'difficulty', max_value: null, startScore: null, description: 'Dificultad de los movimientos', panel: 'D' },
    { key: 'e_score', label: 'Ejecución', type: 'execution', max_value: 10, startScore: 10, description: 'Calidad técnica del movimiento', panel: 'E' },
    { key: 'a_score', label: 'Creatividad', type: 'artistic', max_value: 10, startScore: 10, description: 'Originalidad, fluidez y creatividad', panel: 'A' },
    { key: 'penalty', label: 'Penalización', type: 'penalty', max_value: null, startScore: null, description: 'Deducciones', panel: null },
  ]
};

// ----- PARKOUR SPEED -----
const PARKOUR_SPEED_TEMPLATE = {
  key: 'pk_speed',
  name: 'PK — Parkour Speed',
  discipline: 'parkour',
  category: 'speed',
  formula: 'Tiempo + P',
  description: 'Resultado = Tiempo de carrera + Penalizaciones por infracciones. Menor tiempo gana.',
  panels: {},
  columns: [
    { key: 'time', label: 'Tiempo (seg)', type: 'time', max_value: null, startScore: null, description: 'Tiempo de la carrera en segundos', panel: null },
    { key: 'penalty', label: 'Penalización', type: 'penalty', max_value: null, startScore: null, description: 'Penalizaciones por infracciones (se suma al tiempo)', panel: null },
  ]
};

// ----- GIMNASIA PARA TODOS (GPT) -----
const GPT_TEMPLATE = {
  key: 'gpt_standard',
  name: 'GPT — Gimnasia Para Todos',
  discipline: 'gymnastics_for_all',
  category: null,
  formula: 'D + avg(E) + avg(A) − P',
  description: 'Composición/Dificultad + Promedio Ejecución + Promedio Artístico − Penalizaciones.',
  panels: {
    D: { label: 'Panel D — Composición', judges: 2, method: 'consensus', startScore: null },
    E: { label: 'Panel E — Ejecución', judges: 3, method: 'average', startScore: 10 },
    A: { label: 'Panel A — Artístico', judges: 3, method: 'average', startScore: 10 },
  },
  columns: [
    { key: 'd_score', label: 'Dificultad', type: 'difficulty', max_value: 10, startScore: null, description: 'Composición y dificultad del ejercicio', panel: 'D' },
    { key: 'e_score', label: 'Ejecución', type: 'execution', max_value: 10, startScore: 10, description: 'Ejecución técnica', panel: 'E' },
    { key: 'a_score', label: 'Artístico', type: 'artistic', max_value: 10, startScore: 10, description: 'Presentación artística y coreografía', panel: 'A' },
    { key: 'penalty', label: 'Penalización', type: 'penalty', max_value: null, startScore: null, description: 'Deducciones', panel: null },
  ]
};

// ----- PLANTILLA GENÉRICA -----
const GENERIC_TEMPLATE = {
  key: 'generic',
  name: 'Plantilla Genérica',
  discipline: null,
  category: null,
  formula: 'D + E − P',
  description: 'Dificultad + Ejecución − Penalizaciones. Usar cuando no aplica una plantilla específica.',
  panels: {
    D: { label: 'Dificultad', judges: 1, method: 'single', startScore: null },
    E: { label: 'Ejecución', judges: 1, method: 'single', startScore: 10 },
  },
  columns: [
    { key: 'd_score', label: 'Dificultad', type: 'difficulty', max_value: 10, startScore: null, description: 'Puntuación de dificultad', panel: 'D' },
    { key: 'e_score', label: 'Ejecución', type: 'execution', max_value: 10, startScore: 10, description: 'Puntuación de ejecución', panel: 'E' },
    { key: 'penalty', label: 'Penalización', type: 'penalty', max_value: null, startScore: null, description: 'Deducciones', panel: null },
  ]
};

// =============================================
// MAPA DE PLANTILLAS
// =============================================
const ALL_TEMPLATES = {
  gam_standard: GAM_TEMPLATE,
  gaf_standard: GAF_TEMPLATE,
  aer_standard: AER_TEMPLATE,
  acro_standard: ACRO_TEMPLATE,
  gr_standard: RHYTHMIC_TEMPLATE,
  gt_standard: TRAMPOLINE_TEMPLATE,
  pk_freestyle: PARKOUR_FREESTYLE_TEMPLATE,
  pk_speed: PARKOUR_SPEED_TEMPLATE,
  gpt_standard: GPT_TEMPLATE,
  generic: GENERIC_TEMPLATE,
};

// Disciplina + categoría → template key
const DISCIPLINE_TEMPLATE_MAP = {
  'artistic__wag': 'gaf_standard',
  'artistic__mag': 'gam_standard',
  'artistic__': 'gam_standard',
  'aerobic__': 'aer_standard',
  'acrobatic__': 'acro_standard',
  'rhythmic__': 'gr_standard',
  'trampoline__': 'gt_standard',
  'parkour__speed': 'pk_speed',
  'parkour__freestyle': 'pk_freestyle',
  'parkour__': 'pk_freestyle',
  'gymnastics_for_all__': 'gpt_standard',
};

// =============================================
// API PÚBLICA
// =============================================

/**
 * Obtiene la plantilla estándar para una disciplina/categoría
 */
export function getStandardTemplate(discipline, category) {
  if (!discipline) return GENERIC_TEMPLATE;
  
  // Intento exacto: disciplina__categoría
  const exactKey = `${discipline}__${category || ''}`;
  if (DISCIPLINE_TEMPLATE_MAP[exactKey]) {
    return ALL_TEMPLATES[DISCIPLINE_TEMPLATE_MAP[exactKey]];
  }
  
  // Intento sin categoría
  const baseKey = `${discipline}__`;
  if (DISCIPLINE_TEMPLATE_MAP[baseKey]) {
    return ALL_TEMPLATES[DISCIPLINE_TEMPLATE_MAP[baseKey]];
  }
  
  return GENERIC_TEMPLATE;
}

/**
 * Obtiene todas las plantillas disponibles (para selección manual)
 */
export function getAllTemplates() {
  return ALL_TEMPLATES;
}

/**
 * Obtiene plantillas relevantes para una disciplina
 */
export function getTemplatesForDiscipline(discipline) {
  const results = Object.values(ALL_TEMPLATES).filter(
    t => t.discipline === discipline || t.discipline === null
  );
  return results.length > 0 ? results : [GENERIC_TEMPLATE];
}

// =============================================
// MOTOR DE CÁLCULO — FUNCIONES CORE
// =============================================

/**
 * Media recortada: ordena, quita la más alta y la más baja, promedia el resto
 * Si hay ≤2 valores, hace promedio simple
 */
export function trimmedMean(values) {
  const nums = values.filter(v => v !== null && v !== undefined && !isNaN(v)).map(Number);
  if (nums.length === 0) return 0;
  if (nums.length <= 2) return average(nums);
  
  nums.sort((a, b) => a - b);
  // Quitar la mayor y la menor
  const trimmed = nums.slice(1, -1);
  return trimmed.reduce((sum, v) => sum + v, 0) / trimmed.length;
}

/**
 * Promedio simple
 */
export function average(values) {
  const nums = values.filter(v => v !== null && v !== undefined && !isNaN(v)).map(Number);
  if (nums.length === 0) return 0;
  return nums.reduce((sum, v) => sum + v, 0) / nums.length;
}

/**
 * Calcula el total para un SOLO juez basado en sus columnas.
 * Esto es lo que usa cada juez individual al ingresar puntajes.
 * 
 * Lógica:
 * - Tipos que suman: difficulty, execution, artistic, score, bonus, time_of_flight
 * - Tipos que restan: penalty, deduction, h_displacement
 * - time: se devuelve tal cual (parkour speed)
 */
export function calculateTotal(columns, values) {
  if (!columns || columns.length === 0) return 0;
  
  // Caso especial: Parkour Speed (tiene columna "time")
  const hasTimeCol = columns.find(c => c.type === 'time');
  if (hasTimeCol) {
    const time = parseFloat(values[hasTimeCol.key]) || 0;
    const penalties = columns
      .filter(c => c.type === 'penalty' || c.type === 'deduction')
      .reduce((sum, c) => sum + (parseFloat(values[c.key]) || 0), 0);
    return parseFloat((time + penalties).toFixed(3));
  }
  
  let total = 0;
  columns.forEach(col => {
    const val = parseFloat(values[col.key]) || 0;
    switch (col.type) {
      case 'penalty':
      case 'deduction':
      case 'h_displacement':
        total -= val;
        break;
      case 'difficulty':
      case 'execution':
      case 'artistic':
      case 'score':
      case 'bonus':
      case 'time_of_flight':
        total += val;
        break;
      default:
        total += val;
    }
  });
  
  return Math.max(0, parseFloat(total.toFixed(3)));
}

/**
 * Calcula la nota final combinando scores de MÚLTIPLES jueces.
 * Se usa cuando el organizador/sistema consolida los puntajes de todos los jueces.
 *
 * @param {Object} template - La plantilla de la disciplina (del getStandardTemplate)
 * @param {Object} judgeScores - Mapa { judgeId: { d_score: X, e_score: Y, ... } }
 * @param {Object} options - { useTrim: bool, competitionLevel: string }
 * @returns {{ total, breakdown, details }}
 */
export function calculateFinalScore(template, judgeScores, options = {}) {
  const { useTrim = true } = options;
  const judges = Object.values(judgeScores);
  
  if (judges.length === 0) {
    return { total: 0, breakdown: {}, details: {} };
  }
  
  const breakdown = {};
  const details = {};
  
  // Si solo hay un juez, tomar sus valores directamente
  if (judges.length === 1) {
    const single = judges[0];
    return {
      total: calculateTotal(template.columns, single),
      breakdown: { ...single },
      details: { single_judge: true }
    };
  }
  
  // Agrupar scores por panel
  const panelScores = {}; // { D: [scores], E: [scores], A: [scores] }
  let totalPenalties = 0;
  let timeOfFlight = 0;
  let hDisplacement = 0;
  
  template.columns.forEach(col => {
    if (col.type === 'penalty') {
      // Promediar penalizaciones de todos los jueces
      const penaltyValues = judges.map(j => parseFloat(j[col.key]) || 0);
      totalPenalties = average(penaltyValues);
      details.penalties = penaltyValues;
    } else if (col.type === 'time_of_flight') {
      const tofValues = judges.map(j => parseFloat(j[col.key]) || 0).filter(v => v > 0);
      timeOfFlight = tofValues.length > 0 ? average(tofValues) : 0;
      details.time_of_flight = tofValues;
    } else if (col.type === 'h_displacement') {
      const hdValues = judges.map(j => parseFloat(j[col.key]) || 0);
      hDisplacement = average(hdValues);
      details.h_displacement = hdValues;
    } else if (col.panel) {
      if (!panelScores[col.panel]) panelScores[col.panel] = {};
      if (!panelScores[col.panel][col.key]) panelScores[col.panel][col.key] = [];
      judges.forEach(j => {
        const val = parseFloat(j[col.key]);
        if (!isNaN(val)) panelScores[col.panel][col.key].push(val);
      });
    }
  });
  
  // Calcular cada panel según su método
  let finalScore = 0;
  
  Object.entries(panelScores).forEach(([panelKey, colScores]) => {
    const panelDef = template.panels?.[panelKey];
    const method = panelDef?.method || 'average';
    
    Object.entries(colScores).forEach(([colKey, values]) => {
      let panelResult;
      
      if (method === 'trimmed_mean' && useTrim && values.length >= 3) {
        panelResult = trimmedMean(values);
        details[`${colKey}_method`] = 'trimmed_mean';
      } else if (method === 'consensus') {
        // Paneles D: consensus = promedio (normalmente 2 jueces D acuerdan)
        panelResult = average(values);
        details[`${colKey}_method`] = 'consensus';
      } else {
        panelResult = average(values);
        details[`${colKey}_method`] = 'average';
      }
      
      details[`${colKey}_raw`] = values;
      details[`${colKey}_computed`] = parseFloat(panelResult.toFixed(3));
      breakdown[colKey] = parseFloat(panelResult.toFixed(3));
      finalScore += panelResult;
    });
  });
  
  // Sumar componentes extra
  finalScore += timeOfFlight;
  if (timeOfFlight > 0) breakdown.time_of_flight = parseFloat(timeOfFlight.toFixed(3));
  
  // Restar penalizaciones y desplazamiento
  finalScore -= totalPenalties;
  finalScore -= hDisplacement;
  
  breakdown.penalty = parseFloat(totalPenalties.toFixed(3));
  if (hDisplacement > 0) breakdown.h_displacement = parseFloat(hDisplacement.toFixed(3));
  
  const total = Math.max(0, parseFloat(finalScore.toFixed(3)));
  
  return { total, breakdown, details };
}

/**
 * Para Parkour Speed — menor tiempo gana
 */
export function calculateSpeedResult(judgeScores) {
  const judges = Object.values(judgeScores);
  if (judges.length === 0) return { total: 0, time: 0, penalties: 0 };
  
  const times = judges.map(j => parseFloat(j.time) || 0).filter(v => v > 0);
  const penalties = judges.map(j => parseFloat(j.penalty) || 0);
  
  const bestTime = times.length > 0 ? Math.min(...times) : 0;
  const avgPenalties = average(penalties);
  
  return {
    total: parseFloat((bestTime + avgPenalties).toFixed(3)),
    time: bestTime,
    penalties: parseFloat(avgPenalties.toFixed(3))
  };
}

/**
 * Crea una plantilla personalizada vacía con N columnas
 */
export function createEmptyCustomTemplate(numColumns = 3) {
  const columns = [];
  for (let i = 0; i < numColumns; i++) {
    columns.push({
      key: `col_${i + 1}`,
      label: '',
      type: 'score',
      max_value: 10,
      startScore: null,
      description: '',
      panel: null
    });
  }
  return {
    key: 'custom',
    name: 'Personalizada',
    discipline: null,
    category: null,
    formula: 'Suma de columnas − Deducciones',
    description: 'Plantilla personalizada del juez',
    panels: {},
    columns
  };
}

/**
 * Obtiene un resumen legible del sistema de puntuación para una disciplina
 */
export function getScoringDescription(discipline, category) {
  const template = getStandardTemplate(discipline, category);
  return {
    name: template.name,
    formula: template.formula,
    description: template.description,
    panels: template.panels,
    columns: template.columns.map(c => ({
      label: c.label,
      type: c.type,
      maxValue: c.max_value,
      startScore: c.startScore
    }))
  };
}
